/**
 * Consent Card
 *
 * Agent Coach Lens consent management: status badge, grant/revoke, duration picker.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useManageAgentConsent } from '@/hooks/usePragmatic'
import type { AgentConsentStatus } from '@personic/shared'

interface ConsentCardProps {
  agentId: string
  status: AgentConsentStatus
  expiresAt?: string
  grantedAt?: string
  onStatusChange?: () => void
}

const statusConfig: Record<AgentConsentStatus, { label: string; color: string; bg: string }> = {
  not_requested: { label: 'Not Opted In', color: 'text-gray-600', bg: 'bg-gray-100' },
  granted: { label: 'Active', color: 'text-green-700', bg: 'bg-green-100' },
  revoked: { label: 'Revoked', color: 'text-red-700', bg: 'bg-red-100' },
  expired: { label: 'Expired', color: 'text-amber-700', bg: 'bg-amber-100' },
}

export default function ConsentCard({
  agentId,
  status,
  expiresAt,
  grantedAt,
  onStatusChange,
}: ConsentCardProps) {
  const [durationHours, setDurationHours] = useState(24)
  const manageConsent = useManageAgentConsent(agentId)
  const config = statusConfig[status] || statusConfig.not_requested

  const isActive = status === 'granted'

  const handleGrant = async () => {
    await manageConsent.mutateAsync({
      action: 'grant',
      duration_hours: durationHours,
    })
    onStatusChange?.()
  }

  const handleRevoke = async () => {
    await manageConsent.mutateAsync({ action: 'revoke' })
    onStatusChange?.()
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Coach Lens Consent</h3>
          <p className="text-sm text-gray-500 mt-1">
            Opt in to receive personalized coaching insights
          </p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${config.bg} ${config.color}`}>
          {config.label}
        </span>
      </div>

      {isActive && (
        <div className="p-3 bg-green-50 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span>Coach Lens is active</span>
          </div>
          {grantedAt && (
            <p className="text-xs text-green-600 mt-1">
              Granted: {new Date(grantedAt).toLocaleString()}
            </p>
          )}
          {expiresAt && (
            <p className="text-xs text-green-600">
              Expires: {new Date(expiresAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {!isActive && (
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Consent Duration
            </label>
            <div className="flex items-center gap-3">
              <select
                value={durationHours}
                onChange={(e) => setDurationHours(Number(e.target.value))}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value={1}>1 hour</option>
                <option value={4}>4 hours</option>
                <option value={8}>8 hours (shift)</option>
                <option value={24}>24 hours</option>
                <option value={168}>1 week</option>
                <option value={720}>30 days</option>
              </select>
              <button
                onClick={handleGrant}
                disabled={manageConsent.isPending}
                className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors disabled:opacity-50"
              >
                {manageConsent.isPending ? 'Granting...' : 'Grant Consent'}
              </button>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-500">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>
              You can revoke consent at any time. Analysis stops immediately upon revocation.
              Only you see full insights; managers see aggregate themes only.
            </p>
          </div>
        </div>
      )}

      {isActive && (
        <button
          onClick={handleRevoke}
          disabled={manageConsent.isPending}
          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {manageConsent.isPending ? 'Revoking...' : 'Revoke Consent'}
        </button>
      )}

      {manageConsent.isError && (
        <div className="mt-3 p-2 bg-red-50 text-red-700 text-sm rounded">
          Failed to update consent. Please try again.
        </div>
      )}
    </motion.div>
  )
}
